import Player, { type Options } from "@vimeo/player";
import { createRef, PureComponent } from "react";
import { type CommonProps } from "react-images";

import { withErrorBoundaryForwardRef } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

export interface IVideoPlayerProps {
    vimeoId: number | string | null | undefined;
    vimeoUrl?: string;
    className?: string;
    width?: number;
    height?: number;
    onError?: () => void;
    autoPlay?: boolean;
    "data-testid"?: string;
    modalConfig?: never;
}

export interface IVideoPlayer {
    pause: () => Promise<void>;
}

class VideoPlayerInternal
    extends PureComponent<IVideoPlayerProps & CommonProps>
    implements IVideoPlayer
{
    vimeoElRef = createRef<HTMLDivElement>();
    player: Player | undefined;
    _isMounted: boolean = false;

    async componentDidMount() {
        if (this.vimeoElRef.current) {
            const vimeoId = Number(this.props.vimeoId ?? undefined);
            const playerProps: Options = {
                responsive: true,
                autoplay: this.props.autoPlay,
            };
            if (!isNaN(vimeoId)) {
                playerProps.id = vimeoId;
            }
            if (this.props.vimeoUrl) {
                playerProps.url = this.props.vimeoUrl;
            }

            if (this.props.height) {
                playerProps.height = this.props.height;
            }

            if (this.props.width) {
                playerProps.width = this.props.width;
            }
            this.player = new Player(this.vimeoElRef.current, playerProps);
            this.player.ready().catch((e) => {
                if (this._isMounted) {
                    this.setState(() => {
                        throw e;
                    });
                }
            });
            this._isMounted = true;
        }
    }

    async componentDidUpdate(prevProps: IVideoPlayerProps) {
        const player = this.player;

        if (
            player &&
            this.props.currentView &&
            this.props.currentView.source !== this.props.vimeoId
        ) {
            await player.getPaused().then(async (paused) => {
                if (!paused) {
                    await player.pause();
                }
            });
        }
        if (player && this.props.autoPlay !== prevProps.autoPlay) {
            if (this.props.autoPlay) {
                await player.getPaused().then(async (paused) => {
                    if (paused) {
                        // play() rejects with "PlayInterrupted" / AbortError when pause() is called
                        // or the element unmounts before the play promise resolves.
                        // This is expected browser behaviour; swallow it silently.
                        await player.play().catch((e: unknown) => {
                            if (
                                e instanceof Error &&
                                (e.name === "AbortError" || e.name === "PlayInterrupted")
                            ) {
                                return;
                            }
                            throw e;
                        });
                    }
                });
            } else {
                await player.getPaused().then(async (paused) => {
                    if (!paused) {
                        await player.pause();
                    }
                });
            }
        }
        if (prevProps.vimeoId !== this.props.vimeoId && this.vimeoElRef.current) {
            try {
                await player?.loadVideo(Number(this.props.vimeoId));
            } catch (e) {
                if (this.props.onError) {
                    this.props.onError();
                } else {
                    throw e;
                }
            }
        }
    }

    async componentWillUnmount() {
        this._isMounted = false;
        if (this.player) {
            try {
                await this.player.destroy();
            } catch {
                // Vimeo's destroy() can throw if the player never finished
                // initializing (e.g., invalid id, network failure, unmount
                // race with ready()); cleanup errors are safe to swallow.
            }
        }
    }

    async pause() {
        const player = this.player;
        if (player) {
            await player.pause();
        }
    }

    render() {
        return (
            <div
                className={this.props.className}
                ref={this.vimeoElRef}
                data-testid={this.props["data-testid"]}
            />
        );
    }
}

export const VideoPlayer = withErrorBoundaryForwardRef(VideoPlayerInternal)(
    "Error loading video. Please try again later.",
);
